import { createApi } from '@reduxjs/toolkit/query/react'
import type { AppDispatch, RootState } from '../../app/store'
import type { Location, CreateLocationRequest, UpdateLocationRequest } from '../../types/location'
import { axiosBaseQuery } from '../../services/baseQuery'

type InvalidatePrescriberLocationProfilesOptions = {
  getState: () => RootState
  when: (currentPrescriberId: string) => boolean
}

async function invalidatePrescriberLocationProfilesCaches(
  dispatch: AppDispatch,
  options: InvalidatePrescriberLocationProfilesOptions
): Promise<void> {
  const currentPrescriberId = options.getState().prescriber.prescriber?.id
  if (!currentPrescriberId || !options.when(currentPrescriberId)) return

  const { prescriberLocationProfilesApi } = await import('./prescriberLocationProfilesSlice')
  dispatch(
    prescriberLocationProfilesApi.util.invalidateTags([
      { type: 'PrescriberLocationProfiles', id: 'LIST' },
      { type: 'PrescriberLocationProfiles', id: 'AVAILABLES' },
    ])
  )
}

function getHttpStatusFromMutationLifecycleFailure(error: unknown): number | undefined {
  if (error == null || typeof error !== 'object') return undefined
  const record = error as Record<string, unknown>
  if (typeof record.status === 'number') return record.status
  const nested = record.error
  if (
    nested &&
    typeof nested === 'object' &&
    typeof (nested as Record<string, unknown>).status === 'number'
  ) {
    return (nested as Record<string, unknown>).status as number
  }
  return undefined
}

interface UpdateLocationQueryObj {
  locationId: string
  location: UpdateLocationRequest
}

interface AssignPrescribersQueryObj {
  locationId: string
  prescriberIds: string[]
}

export const locationsApi = createApi({
  reducerPath: 'locationsApi',
  tagTypes: ['Locations'],
  baseQuery: axiosBaseQuery({ baseUrl: '/bff/locations' }),
  keepUnusedDataFor: 3600,
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  endpoints: (build) => ({
    getLocations: build.query<Location[], void>({
      query: () => ({
        url: '',
        method: 'get',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Locations' as const, id })),
              { type: 'Locations', id: 'LIST' },
            ]
          : [{ type: 'Locations', id: 'LIST' }],
    }),
    createLocation: build.mutation<Location, CreateLocationRequest>({
      query: (newLocation) => ({
        url: '',
        method: 'post',
        data: newLocation,
      }),
      invalidatesTags: (_result, error) => (error ? [] : [{ type: 'Locations', id: 'LIST' }]),
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled
          await invalidatePrescriberLocationProfilesCaches(dispatch, {
            getState: getState as () => RootState,
            when: () => true,
          })
        } catch {
          /* create failed — leave prescriber caches unchanged */
        }
      },
    }),
    updateLocation: build.mutation<Location, UpdateLocationQueryObj>({
      query: ({ locationId, location }) => ({
        url: `/${locationId}`,
        method: 'patch',
        data: location,
      }),
      invalidatesTags: (_result, error, { locationId }) =>
        error
          ? []
          : [
              { type: 'Locations', id: locationId },
              { type: 'Locations', id: 'LIST' },
            ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled
          await invalidatePrescriberLocationProfilesCaches(dispatch, {
            getState: getState as () => RootState,
            when: () => true,
          })
        } catch {
          /* update failed — leave prescriber location profile cache unchanged */
        }
      },
    }),
    assignPrescribers: build.mutation<Location, AssignPrescribersQueryObj>({
      query: ({ locationId, prescriberIds }) => ({
        url: `/${locationId}/prescribers`,
        method: 'post',
        data: { prescriberIds },
      }),
      invalidatesTags: (_result, error, { locationId }) => {
        const status = (error as { status?: number } | undefined)?.status
        if (error && status !== 409) {
          return []
        }
        return [
          { type: 'Locations', id: locationId },
          { type: 'Locations', id: 'LIST' },
        ]
      },
      async onQueryStarted({ prescriberIds }, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled
        } catch (error) {
          /* 409 = partial success (see invalidatesTags); still refresh profiles if current user was assigned */
          if (getHttpStatusFromMutationLifecycleFailure(error) !== 409) return
        }
        await invalidatePrescriberLocationProfilesCaches(dispatch, {
          getState: getState as () => RootState,
          when: (id) => prescriberIds.includes(id),
        })
      },
    }),
    removePrescriber: build.mutation<Location, { locationId: string; prescriberId: string }>({
      query: ({ locationId, prescriberId }) => ({
        url: `/${locationId}/prescribers/${prescriberId}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, error, { locationId }) =>
        error
          ? []
          : [
              { type: 'Locations', id: locationId },
              { type: 'Locations', id: 'LIST' },
            ],
      async onQueryStarted({ prescriberId }, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled
          await invalidatePrescriberLocationProfilesCaches(dispatch, {
            getState: getState as () => RootState,
            when: (id) => id === prescriberId,
          })
        } catch {
          /* remove failed — leave prescriber location profile cache unchanged */
        }
      },
    }),
  }),
})

export const {
  useGetLocationsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useAssignPrescribersMutation,
  useRemovePrescriberMutation,
} = locationsApi
